import {
  Box,
  Button,
  DarkMode,
  Divider,
  Image,
  LightMode,
  Link,
  Stack,
  Text,
  Wrap,
} from "@chakra-ui/react";
import { RGrid } from "@raiden/library/components/RGrid";
import { POSTS_TYPE_VALUE_PAGE } from "@raiden/library/constants/posts";
import { useConfiguration } from "@raiden/library/hooks/useConfiguration";
import useTranslate from "@raiden/library/hooks/useTranslate";
import generateFrontPath from "@raiden/library/libraries/utils/generateFrontPath";
import { FormattedMessage, useIntl } from "react-intl";
import { CenteredContent } from "../../../../components/CenteredContent";
import { Href } from "../../../../components/Href";
import { PersonnalInformation } from "../../../ContactUs/Information";
import { Menu } from "./Menu";
import { Partners } from "./Partners";
import { Section } from "./Section";
import { SocialNetworks } from "./SocialNetworks";

export function Footer() {
  const intl = useIntl();

  const translate = useTranslate();

  const { configuration } = useConfiguration();

  const environment = configuration.environments?.[0];

  const postLegalNotice = configuration.posts?.[POSTS_TYPE_VALUE_PAGE]?.find(
    (post) => post.alias === process.env.NEXT_PUBLIC_POST_ALIAS_LEGAL_NOTICE,
  );

  return (
    <DarkMode>
      <Box px="1rem" py="2rem">
        <CenteredContent>
          <Partners />
        </CenteredContent>
      </Box>

      <Box>
        <CenteredContent maxWidth="1920px">
          <Box
            backgroundImage={`url(${generateFrontPath({
              id: "@front.internal-assets",
              parameters: {
                filePath: "montains-dark.svg",
              },
              includeBasePath: true,
            })})`}
            backgroundSize="auto 100%"
            backgroundPosition="center"
            px="1rem"
            pt={{ base: "10rem", md: "6rem" }}
            pb="1rem">
            <CenteredContent>
              <RGrid minCellWidth="350px" gridGap="1.5rem">
                <Section
                  title={intl.formatMessage({
                    defaultMessage: "La mairie vous accueille",
                  })}>
                  <Stack spacing="0.75rem">
                    <PersonnalInformation environment={environment} />

                    <Box>
                      <LightMode>
                        <Href
                          href={generateFrontPath({ id: "@front.contact" })}>
                          <Button colorScheme="gray" h="48px">
                            <Text as="span" variant="button">
                              <FormattedMessage defaultMessage="Nous contacter" />
                            </Text>
                          </Button>
                        </Href>
                      </LightMode>
                    </Box>
                  </Stack>
                </Section>

                <Section
                  title={intl.formatMessage({
                    defaultMessage: "Suivez nos actualités",
                  })}>
                  <Stack spacing="1.5rem">
                    <Menu />

                    <SocialNetworks />
                  </Stack>
                </Section>

                <Section
                  title={intl.formatMessage({
                    defaultMessage: "L'application mobile",
                  })}>
                  <Stack spacing="1rem">
                    <Text>
                      <FormattedMessage defaultMessage="Suivez les actualités de votre ville directement sur votre téléphone avec l'application La Bresse Hautes Vosges" />
                    </Text>

                    <Wrap spacing="1rem">
                      <Href
                        href="https://play.google.com/store/apps/details?id=fr.laBresse.mobile&hl=fr_CH"
                        target="_blank">
                        <Image
                          src={generateFrontPath({
                            id: "@front.internal-assets",
                            parameters: { filePath: "appstore-google.png" },
                            includeBasePath: true,
                          })}
                          alt="Google Play"
                        />
                      </Href>

                      <Href
                        href="https://apps.apple.com/fr/app/la-bresse-hautes-vosges/id1595849299"
                        target="_blank">
                        <Image
                          src={generateFrontPath({
                            id: "@front.internal-assets",
                            parameters: { filePath: "appstore-apple.png" },
                            includeBasePath: true,
                          })}
                          alt="App Store"
                        />
                      </Href>
                    </Wrap>
                  </Stack>
                </Section>
              </RGrid>

              <Divider borderColor="brandPrimary.500" mt="3.125rem" />

              <Wrap
                spacing="1rem"
                justify={{ base: "center", lg: "space-evenly" }}
                mt="24px">
                <Text textAlign="center">
                  <FormattedMessage
                    defaultMessage="© {year} Mairie de La Bresse - Tous droits réservés"
                    values={{
                      year: new Date().getFullYear(),
                    }}
                  />
                </Text>

                <Text textAlign="center">
                  <FormattedMessage
                    defaultMessage="Conception et réalisation par <link>Splitfire</link> à Épinal"
                    values={{
                      link: (chunks) => (
                        <Href href="https://www.splitfire.fr" target="_blank">
                          <Link
                            as="span"
                            textDecoration="underline"
                            color="#fff">
                            {chunks}
                          </Link>
                        </Href>
                      ),
                    }}
                  />
                </Text>

                {postLegalNotice && (
                  <Href
                    href={generateFrontPath({
                      id: "@front.page",
                      parameters: {
                        postSlug: `${postLegalNotice.id}-${translate(
                          postLegalNotice.slug,
                        )}`,
                      },
                    })}>
                    <Link as="span" textDecoration="underline" color="#fff">
                      <FormattedMessage defaultMessage="Mentions légales" />
                    </Link>
                  </Href>
                )}
              </Wrap>
            </CenteredContent>
          </Box>
        </CenteredContent>
      </Box>
    </DarkMode>
  );
}
